// frontend/src/components/common/LoadingSpinner.jsx

import React from "react";

/**
 * LoadingSpinner — Reusable loading indicator
 *
 * A simple, clean loading spinner with optional text.
 * Used throughout the ManejaRD application to indicate loading states.
 *
 * Sizes:
 * - 'sm': 16px — for inline use (inside buttons, table cells)
 * - 'md': 32px — standard component loading
 * - 'lg': 48px — page-level loading
 * - 'xl': 64px — full-page loading screens
 *
 * Props:
 * @param {'sm'|'md'|'lg'|'xl'} size - Spinner size
 * @param {string} text - Optional loading text displayed below spinner
 * @param {string} className - Additional CSS classes
 */
const LoadingSpinner = ({ size = "md", text = "", className = "" }) => {
  // Size mapping for the spinner dimensions
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-8 w-8",
    lg: "h-12 w-12",
    xl: "h-16 w-16",
  };

  // Size mapping for the text below the spinner
  const textSizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
    xl: "text-lg",
  };

  // Stroke width varies by size for visual balance
  const strokeWidth = size === "sm" ? "3" : "2";

  const spinnerSize = sizeClasses[size] || sizeClasses.md;
  const textSize = textSizeClasses[size] || textSizeClasses.md;

  return (
    <div
      className={`flex flex-col items-center justify-center ${className}`}
      role="status"
      aria-label={text || "Cargando"}
      aria-live="polite"
    >
      {/* Spinning circle */}
      <svg
        className={`animate-spin ${spinnerSize} text-primary`}
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth={strokeWidth}
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>

      {/* Optional loading text */}
      {text && <p className={`mt-2 text-gray-500 ${textSize}`}>{text}</p>}

      {/* Screen reader only text (when no visible text) */}
      {!text && <span className="sr-only">Cargando...</span>}
    </div>
  );
};

export default LoadingSpinner;
